/**
 * Bug Fix API
 *
 * This module handles API calls for inserting/removing the DAZN bug overlay on a
 * live event using AWS Step Functions.
 */

import ApiConfig from "../services/ApiConfig";
import axios from "axios";
import environmentMapping from "../config/environmentMapping.json";

/**
 * Insert or remove the DAZN bug overlay by invoking the appropriate Step Function
 *
 * @param {string|string[]} eventId - The event ID or array of event IDs
 * @param {string} environment - The environment (short code or full name)
 * @param {string} bug - The bug action ("insert" or "removal")
 * @param {string} overlay - The overlay file name, required when bug is "insert"
 * @returns {Promise<Object>} Response object with success/error information
 */
const bugFixApi = async (eventId, environment, bug, overlay = "") => {
    try {
        // Validate inputs
        if (!eventId || !environment || !bug) {
            throw new Error("Invalid parameters: eventId, environment, and bug are required");
        }

        if (bug !== "insert" && bug !== "removal") {
            throw new Error("Invalid bug value: bug must be 'insert' or 'removal'");
        }

        if (bug === "insert" && !overlay) {
            throw new Error("Invalid parameters: overlay is required when bug is 'insert'");
        }

        // Ensure eventId is an array
        const eventIdList = Array.isArray(eventId) ? eventId : [eventId];

        // Map short environment codes to full names
        const fullEnvironmentName = environmentMapping[environment] || environment;

        // Get API configuration
        var config = await ApiConfig();
        config.method = "post";
        config.url += "/bug_fix_live_event";

        // Prepare request payload
        config.data = {
            environment: fullEnvironmentName,
            eventIdList: eventIdList,
            bug: bug,
            overlay: overlay
        };

        // Make the API call
        return await axios(config)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                // Handle different types of errors
                if (error.response) {
                    // Server responded with error status
                    const status = error.response.status;
                    const data = error.response.data;

                    // Handle specific error cases
                    if (status === 409 && data && data.alreadyExists) {
                        return {
                            message: `Bug ${bug} already in progress for this event`,
                            alreadyExists: true,
                            executionArn: data.executionArn || null
                        };
                    } else if (status === 403) {
                        throw new Error("Access denied to Step Function. Please check IAM permissions or contact the administrator.");
                    } else if (status === 500) {
                        // 500 can be infrastructure errors (like missing cross-account role) or server errors
                        const message = data?.message || data?.error || "An internal server error occurred. Please try again or contact support.";
                        throw new Error(message);
                    } else if (status === 400) {
                        // Generic bad request message
                        const message = data?.message || data?.error || "Invalid request. Please check your input and try again.";
                        throw new Error(message);
                    } else if (data && data.message) {
                        throw new Error(data.message);
                    } else {
                        throw new Error(`Server error (${status}). Please try again or contact support.`);
                    }
                } else if (error.request) {
                    // Request was made but no response received (network error)
                    throw new Error("Network error. Please check your connection and try again.");
                } else {
                    // Something else happened (error in request setup)
                    throw new Error(error.message || "An unexpected error occurred. Please try again.");
                }
            });
    } catch (error) {
        console.error("Error in bugFixApi:", error);
        throw error;
    }
};

export default bugFixApi;
